import 'lib/init'
import 'lib/experiments_api_init'
import 'shared/i18n/init'

import { ColorTheme, ThemeProvider } from '@figma/fpl-themes'
import { t } from '@figma/i18n/translation'
import {
  authDocumentTitleLoginFigma,
  authDocumentTitleSignUpForFigma,
} from '@figma/i18n-dictionaries/en/web/auth'
import { FigmaTeam } from '@figma/ts-lib/teams'
import history from '@web/lib/history'
import { AuthFormState } from '@web/redux/auth'
import type { Store } from 'redux'

import { desktopApp } from 'lib/desktop_app'
import { initializeDesktopLocale } from 'lib/desktop_app_locale'
import { redirectIfNoAuthOnDesktop } from 'lib/desktop_utils'
import { AtomsProvider } from 'lib/init/atoms_provider'
import { figmaReactRender } from 'lib/init/figma_react_render'
import { ReduxProvider } from 'lib/init/redux_provider'
import { StatsigProvider } from 'lib/init/statsig_provider'
import { extractPageLocales } from 'lib/initial_options'
import { initializeFlash } from 'lib/initialize_flash'
import { LoadTimerDynamicConfigSettingsProvider } from 'lib/load_timer_dynamic_config'
import { initMetricTracker } from 'lib/web_vitals_reporting'
import { isInitialAuthFormState } from 'shared/auth/auth_models'
import { initAuth } from 'shared/auth/init_auth'
import { AuthApplicationService } from 'shared/auth/service/auth_application_service'
import { initDayJSWithLocale } from 'shared/i18n/dayjs_helpers'
import { fetchHardcodedDict } from 'shared/i18n/fetch_hardcoded_dicts'
import { initI18nStateWithLocale } from 'shared/i18n/helpers'

import { authApp } from './auth_app_reducers'
import { AuthApp } from './views/auth_app_view'

initMetricTracker('auth')

function initAuthStore(store: Store) {
  const params = new URLSearchParams(history.location.search)
  const origin = params.get('origin') || 'auth_page'
  const queryParamFormState = params.get('form_state')
  const formState: AuthFormState | undefined =
    queryParamFormState && isInitialAuthFormState(queryParamFormState)
      ? queryParamFormState
      : undefined

  initAuth(store, {
    origin,
    redirectUrl: params.get('redirect_url') || undefined,
    formState,
  })
  store.dispatch(initializeFlash())

  if (desktopApp) {
    desktopApp.setMessageHandler((...[name, args]) => {
      if (name === 'redeemAppAuth') {
        AuthApplicationService.create(store).appAuth.redeem(args.gSecret)
      }
    })
    initializeDesktopLocale()
  }

  // Read the resolved form state from the store rather than re-deriving it from
  // `formState`/`initialOptions().form_state`: the `init` reducer coerces VERIFY_HUMAN
  // away and defaults to SIGN_UP, so the raw server value can still be VERIFY_HUMAN here
  // and would leave the title mismatched with the form the user actually sees.
  const populatedFormState =
    AuthApplicationService.create(store).authLifecycle.getCurrentFormState()

  // Set the document title for the login & sign up pages
  // NB: This is done server-side also via passing in `page_title_override` to the ERB template, but
  // we do it here as well to support locales that the product is not localized in.
  if (populatedFormState === AuthFormState.SIGN_UP) {
    document.title = t(authDocumentTitleSignUpForFigma)
  } else if (populatedFormState === AuthFormState.SIGN_IN) {
    document.title = t(authDocumentTitleLoginFigma)
  }
}

function WrappedAppView() {
  return <AuthApp />
}

figmaReactRender(
  () => {
    if (redirectIfNoAuthOnDesktop()) return null
    return (
      <ThemeProvider>
        <ColorTheme brand="cosmos" mode="light">
          <ReduxProvider reducer={authApp} onCreate={initAuthStore}>
            <AtomsProvider>
              <LoadTimerDynamicConfigSettingsProvider>
                <StatsigProvider>
                  <WrappedAppView />
                </StatsigProvider>
              </LoadTimerDynamicConfigSettingsProvider>
            </AtomsProvider>
          </ReduxProvider>
        </ColorTheme>
      </ThemeProvider>
    )
  },
  {
    team: FigmaTeam.ACQUISITION,
    i18nDelegate: async () => {
      await fetchHardcodedDict(extractPageLocales()[0]!)
      await initI18nStateWithLocale()
      initDayJSWithLocale()
    },
  },
)
